/**
 * Raw HTTP calls to the BFF's auth endpoints — login and password rotation.
 *
 * Pure with respect to React and Supabase: each function takes an explicit
 * `fetch` implementation and returns a closed set of outcomes, so a test can
 * drive the 401/429/500/network-failure branches without a network and
 * without mounting anything.
 *
 * This module only classifies what the network said — it does not decide
 * what the customer sees. Error-key mapping (the no-enumeration invariant of
 * ADR-0003) lives one layer up, in `auth/loginFlow.ts` and
 * `auth/rotateFlow.ts`.
 */

export type FetchLike = typeof fetch;

export interface LoginTokens {
  access_token: string;
  refresh_token: string;
}

export type LoginBffOutcome =
  | { kind: 'ok'; tokens: LoginTokens }
  | { kind: 'rate_limited' }
  | { kind: 'rejected' }
  | { kind: 'malformed' }
  | { kind: 'transport_error' };

/**
 * ⚠️ The password grant goes through the BFF, NOT through
 *    supabase.auth.signInWithPassword.
 *
 *    signInWithPassword talks to GoTrue directly, which would leave the BFF
 *    out of the login path — and with it the per-documento (5 / 15 min) and
 *    per-IP (20 / 15 min) lockout that ADR-0003 requires to be enforced IN
 *    FRONT OF GoTrue. With an activation credential that is the first 6
 *    characters of the CPF, unlimited attempts opens every unactivated
 *    account. The edge also rejects the password grant now, so calling
 *    GoTrue directly from here would simply 404.
 */
export async function requestLogin(
  fetchImpl: FetchLike,
  bffBase: string,
  documento: string,
  password: string,
): Promise<LoginBffOutcome> {
  let res: Response;
  try {
    res = await fetchImpl(`${bffBase}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documento, password }),
    });
  } catch {
    // The request never reached the BFF — offline, or the edge is down.
    return { kind: 'transport_error' };
  }

  if (res.status === 429) {
    return { kind: 'rate_limited' };
  }

  if (!res.ok) {
    // Everything else is "rejected" — the BFF already returns one uniform
    // 401 for "no such documento", "wrong password" and "expired activation
    // credential"; the caller must not reintroduce the difference (ADR-0003).
    return { kind: 'rejected' };
  }

  // The BFF returns GoTrue's token payload unchanged.
  const payload = (await res.json().catch(() => ({}))) as Partial<LoginTokens>;

  if (!payload.access_token || !payload.refresh_token) {
    return { kind: 'malformed' };
  }

  return { kind: 'ok', tokens: { access_token: payload.access_token, refresh_token: payload.refresh_token } };
}

export type RotateBffOutcome =
  /** `session` is the fresh grant the BFF minted AFTER revoking every prior
   *  session — including the one this request was made with. Absent means the
   *  rotation succeeded but re-authentication did not; the caller must not
   *  keep using its current token, which is dead. */
  | { kind: 'ok'; session?: LoginTokens }
  | { kind: 'forbidden' }
  | { kind: 'rejected'; code: string }
  | { kind: 'transport_error' };

export async function requestRotatePassword(
  fetchImpl: FetchLike,
  bffBase: string,
  accessToken: string,
  newPassword: string,
): Promise<RotateBffOutcome> {
  try {
    const res = await fetchImpl(`${bffBase}/rotate-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ password: newPassword }),
    });

    if (res.ok) {
      const body = (await res.json().catch(() => ({}))) as { session?: Partial<LoginTokens> };
      const t = body.session;
      if (t && typeof t.access_token === 'string' && typeof t.refresh_token === 'string') {
        return { kind: 'ok', session: { access_token: t.access_token, refresh_token: t.refresh_token } };
      }
      return { kind: 'ok' };
    }
    if (res.status === 403) return { kind: 'forbidden' };

    // Stable error codes get mapped to client-friendly messages one layer up
    // (`rotateFlow.ts`); here we just carry the code through.
    const body = (await res.json().catch(() => ({}))) as { code?: string };
    return { kind: 'rejected', code: body.code ?? '' };
  } catch {
    return { kind: 'transport_error' };
  }
}
